import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Eye, Check, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';

const abs = (u) => (!u ? null : u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

/* Prepared set of 10 random QC passes for one style, one at a time beside the reference. Logs agreement only — never approves. */
export const SpotCheckDialog = ({ api, headers, style, onLogged }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [i, setI] = useState(0);
  const [busy, setBusy] = useState(false);
  const [tally, setTally] = useState({ ok: 0, wrong: 0 });

  const load = useCallback(async () => {
    setData(null);
    try {
      const { data: d } = await axios.get(`${api}/qc/spot-check`, { headers, params: { style } });
      const first = d.items.findIndex(x => !x.spot_result);
      setI(first === -1 ? d.items.length : first);
      setTally({ ok: d.items.filter(x => x.spot_result === 'spot_ok').length, wrong: d.items.filter(x => x.spot_result === 'flag_wrong').length });
      setData(d);
    } catch { toast.error('Could not load spot-check set'); }
  }, [api, headers, style]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const item = data?.items?.[i];
  const total = data?.items?.length || 0;
  const decide = async (ok) => {
    let body = { source: 'spot_check' };
    if (!ok) {
      const note = window.prompt(`What is wrong with ${item.color_name}? (optional)`, '');
      if (note === null) return;
      body.note = note;
    }
    setBusy(true);
    try {
      await axios.post(`${api}/${item.id}/${ok ? 'spot-ok' : 'qc-wrong'}`, ok ? {} : body, { headers });
      setTally(t => ({ ok: t.ok + (ok ? 1 : 0), wrong: t.wrong + (ok ? 0 : 1) }));
      onLogged?.();
      if (i + 1 >= total) load(); else setI(n => n + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not log'); }
    setBusy(false);
  };

  useEffect(() => {
    if (!open || !item) return undefined;
    const onKey = (e) => {
      if (busy || e.metaKey || e.ctrlKey || e.altKey || ['INPUT', 'TEXTAREA'].includes(e.target?.tagName)) return;
      const k = e.key.toLowerCase();
      if (k === 'r') { e.preventDefault(); decide(true); }
      if (k === 'w') { e.preventDefault(); decide(false); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, item, busy]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="mt-1 inline-flex items-center gap-1 text-neutral-700 hover:underline" data-testid={`spot-check-btn-${style}`}><Eye className="w-3 h-3" />Spot-check</button>
      </DialogTrigger>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="capitalize">Spot-check · {style} QC passes</DialogTitle></DialogHeader>
        <p className="text-xs text-neutral-500 -mt-2">Prepared set of random QC passes. "Looks right" and "QC wrong" only feed the QC Accuracy Log — nothing is approved or replaced.</p>
        {!data ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-neutral-400" /></div>
          : !item ? (
            <div className="py-10 text-center space-y-2" data-testid="spot-check-done">
              <p className="text-sm text-neutral-800">{total ? `Set done — ${tally.ok} looked right, ${tally.wrong} QC wrong. See the Spot-check summary for this style's status.` : 'No unchecked QC passes left for this style.'}</p>
              
            </div>
          ) : (
            <div className="space-y-3" data-testid="spot-check-item">
              <div className="grid grid-cols-2 gap-3">
                {[['Approved reference', data.reference_url], [`${item.color_name} · ${item.collection_name || ''}`, item.url]].map(([label, url]) => (
                  <div key={label} className="space-y-1">
                    <p className="text-xs font-medium text-neutral-700 truncate">{label}</p>
                    <a href={abs(url)} target="_blank" rel="noreferrer"><img src={abs(url)} alt={label} className="w-full aspect-square object-cover rounded-md border border-neutral-200" /></a>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-green-700" data-testid="spot-check-qc-reason">QC PASS — {item.qc?.reason}</p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-neutral-500" data-testid="spot-check-progress">{i + 1} / {total} in this set · {tally.ok} looked right · {tally.wrong} QC wrong</span>
                <Button size="sm" className="ml-auto gap-1 bg-green-700 hover:bg-green-800" disabled={busy} onClick={() => decide(true)} data-testid="spot-check-ok"><Check className="w-3.5 h-3.5" />Looks right <kbd className="ml-1 rounded bg-white/20 px-1 text-[10px]">R</kbd></Button>
                <Button size="sm" variant="outline" className="gap-1 text-red-700" disabled={busy} onClick={() => decide(false)} data-testid="spot-check-wrong"><X className="w-3.5 h-3.5" />QC wrong <kbd className="ml-1 rounded bg-neutral-100 px-1 text-[10px]">W</kbd></Button>
              </div>
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
};
